import { arrayRemove, arrayUnion, doc, getDoc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import { FUSO_PADRAO, minutosUTC, type Lembretes } from "@nhomi/core";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./ambiente";
import type { UserDoc } from "./schema";

/* ============================================================
   Perfil

   `nickname` e `customPhoto` são marcas, não valores: elas dizem "esta
   pessoa escolheu isto à mão". Sem elas, o `ensureUser` — que roda a
   cada login pra manter o perfil em dia com o Google — sobrescreveria o
   apelido de volta pro nome da conta na próxima abertura do app.

   O nome e a foto ficam repetidos na ficha de membro de cada desafio,
   em cada prato publicado, em cada comentário e em cada recado do
   bate-papo. Isso é de propósito: o feed mostra dez autores e ler dez
   perfis por tela seria caro. Quem espalha a mudança é a função — o
   cliente escreve num lugar só —, e quem GRAVA o nome e a foto junto
   de um documento lê daqui também (`perfilQueVale`), porque o `me`
   que a tela tem é o do login: a troca em Conta não o atualiza.
   ============================================================ */

export interface Perfil extends UserDoc { uid: string }

const userRef = (uid: string) => doc(db, "users", uid);

/**
 * Uma leitura só, para quem vai ESCREVER em seguida.
 *
 * `salvarLembretes` grava o objeto inteiro. Quem monta o objeto a partir
 * do padrão em vez do que está gravado apaga, sem querer, o que a pessoa
 * já tinha configurado — e apagar configuração é o tipo de perda que
 * ninguém relaciona com o botão que a causou.
 */
export async function lerPerfil(uid: string): Promise<Perfil | null> {
  const s = await getDoc(userRef(uid));
  return s.exists() ? { uid: s.id, ...(s.data() as UserDoc) } : null;
}

export function watchUser(uid: string, cb: (p: Perfil | null) => void): () => void {
  return onSnapshot(
    userRef(uid),
    (s) => cb(s.exists() ? { uid: s.id, ...(s.data() as UserDoc) } : null),
    () => cb(null),
  );
}

/**
 * O nome e a foto que valem para GRAVAR são os de `users/{uid}`, não os
 * do login.
 *
 * O `me` que as telas montam vem do Firebase Auth, que a troca de
 * apelido e de foto em Conta nunca atualiza — ela escreve só no
 * documento, e quem espalha é a função. Gravar `me.name`/`me.photo`
 * era espalhar o nome e a foto ORIGINAIS do Google por cima dos
 * escolhidos: a ficha que voltava ao nome de batismo quando a pessoa
 * entrava de novo no desafio nasceu aqui.
 *
 * A leitura é barata porque o documento já está quente no cache — o
 * `watchUser` do provedor do plano o mantém aberto. Sem ela (offline,
 * documento ainda não criado) fica o `me`, que não é pior que o de
 * hoje.
 */
export async function perfilQueVale(
  me: { uid: string; name: string; photo: string },
): Promise<{ uid: string; name: string; photo: string }> {
  try {
    const perfil = await lerPerfil(me.uid);
    if (perfil) {
      return {
        uid: me.uid,
        name: perfil.name || me.name,
        photo: perfil.photo || me.photo,
      };
    }
  } catch { /* offline: fica o da sessão */ }
  return { uid: me.uid, name: me.name, photo: me.photo };
}

export interface EdicaoDePerfil {
  name?: string;
  photo?: string;
}

/**
 * Salva o perfil e marca o que foi escolhido à mão.
 *
 * As marcas só sobem, nunca descem: quem escolheu um apelido uma vez
 * continua com o dele mesmo que depois volte a digitar o nome do Google.
 */
export async function updateProfile(uid: string, patch: EdicaoDePerfil): Promise<void> {
  const campos: Record<string, unknown> = {};
  if (patch.name !== undefined) { campos["name"] = patch.name; campos["nickname"] = true; }
  if (patch.photo !== undefined) { campos["photo"] = patch.photo; campos["customPhoto"] = true; }
  if (!Object.keys(campos).length) return;
  await updateDoc(userRef(uid), campos);
}

/* ---------- avisos ---------- */

export interface Avisos {
  /** Alguém do desafio postou um prato. */
  plates: boolean;
  /** Comentaram num prato seu. */
  comments: boolean;
  /** Deram nota num prato seu. */
  ratings: boolean;
  /** Mandaram mensagem no bate-papo. */
  chat: boolean;
  /** Você cravou o preço ou a caloria de um prato alheio. */
  guesses: boolean;
  /** Falta só você, cutucadas, sequência acabando, te passaram, fim da semana. */
  disputa: boolean;
  /** Alguém fechou uma missão; coletiva perto de fechar. */
  missoes: boolean;
}

export const AVISOS_PADRAO: Avisos = {
  plates: true, comments: true, ratings: true, chat: false, guesses: true,
  disputa: true, missoes: true,
};

export const AVISOS_ROTULOS: { id: keyof Avisos; label: string; hint: string }[] = [
  { id: "plates", label: "Pratos novos", hint: "Quando alguém do desafio posta" },
  { id: "comments", label: "Comentários", hint: "Quando comentam num prato seu" },
  { id: "ratings", label: "Notas", hint: "Quando dão nota num prato seu (sem dizer quem, nem quanto)" },
  { id: "chat", label: "Bate-papo", hint: "Toda mensagem nova do grupo" },
  { id: "guesses", label: "Cravadas", hint: "Quando você acerta o preço ou a caloria de um prato" },
  { id: "disputa", label: "Disputa", hint: "Falta só você, cutucadas, sequência acabando e quem te passou no placar (só à noite)" },
  { id: "missoes", label: "Missões", hint: "Quando alguém fecha uma missão ou o grupo está quase lá" },
];

/* ---------- silenciar um grupo ---------- */

/**
 * Silencia (ou volta a ouvir) TODOS os avisos de um grupo. Quem está em
 * vários grupos escolhe de quais recebe; os lembretes de refeição são
 * da pessoa, não do grupo, e continuam.
 */
export async function silenciarGrupo(uid: string, cid: string, silenciar: boolean): Promise<void> {
  await setDoc(userRef(uid), {
    silenciados: silenciar ? arrayUnion(cid) : arrayRemove(cid),
  }, { merge: true });
}

export async function saveAvisos(uid: string, avisos: Avisos): Promise<void> {
  await setDoc(userRef(uid), { notify: avisos }, { merge: true });
}

/* ---------- lembretes de refeição ---------- */

/**
 * Salva os lembretes E a chave que o servidor usa para achar a pessoa.
 *
 * Os dois SEMPRE na mesma escrita. Separados, uma delas falha sozinha e
 * o resultado é o pior defeito possível num lembrete: a tela diz "todo
 * dia às 12h" e nada chega, sem erro em lugar nenhum. É o mesmo tipo de
 * mentira que o `push.ts` evita ao chamar de "ligadas" só o que está
 * gravado no banco.
 *
 * O `fuso` é sempre o do aparelho que está salvando: quem mexe no
 * horário está, por definição, onde vai receber.
 */
export async function salvarLembretes(uid: string, lembretes: Lembretes): Promise<void> {
  const completo: Lembretes = { ...lembretes, fuso: fusoDoAparelho() };
  await setDoc(userRef(uid), {
    lembretes: completo,
    lembretesUTC: minutosUTC(completo),
  }, { merge: true });
}

export const fusoDoAparelho = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FUSO_PADRAO;
  } catch {
    return FUSO_PADRAO;
  }
};

/* ---------- apagar a conta ---------- */

/**
 * Apaga a conta e o rastro dela.
 *
 * Callable, e não escrita daqui, por dois motivos que se somam: apagar o
 * login exige o Admin SDK, e o rastro de uma pessoa está espalhado por
 * desafios onde as regras não deixariam esta tela escrever — a ficha do
 * placar, a lista de membros, o dono do desafio.
 *
 * A palavra de confirmação viaja junto: é o servidor quem recusa se ela
 * não vier, não a tela. Confirmação que só existe no cliente é decoração.
 */
export async function apagarConta(confirmacao: string): Promise<void> {
  const chamar = httpsCallable<{ confirmacao: string }, { ok: boolean }>(
    functions, "nhomiApagarConta",
  );
  await chamar({ confirmacao });
}

export const PALAVRA_PARA_APAGAR = "APAGAR";
